import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchOrCached, isOfflineGap, NOT_ON_THIS_PC } from '@/offline/availability';
import { LoadingNote, OfflineDetails } from '@/components/OfflineDetails';
import { useSavedWork } from '@/offline/useSavedWork';
import { useEffect, useRef, useState } from 'react';
import { api, UNREACHABLE_MESSAGE } from '@/api/client';
import {
  ActionBar,
  DataGrid,
  type DesktopAction,
  type GridColumn,
} from '@/components/desktop';
import { AppIcon } from '@/components/AppIcon';
import { ComposePanel } from '@/components/ComposePanel';
import { DocumentFrame } from '@/components/DocumentFrame';
import { SavedWorkDetail } from '@/components/SavedWorkDetail';
import { DraftNotice } from '@/components/DraftNotice';
import { useDraft } from '@/stores/drafts';
import {
  DeleteButton,
  HistoryPanel,
  ReverseButton,
  ReviewSummaryDialog,
  printDocument,
} from '@/components/documents';
import { ItemLines, ItemTotals, emptyReceiptLine, lineTotal, type ReceiptLine } from '@/components/ItemLines';
import { ENTRY_ROWS, padRows } from '@/components/lineRows';
import {
  distinctCount,
  isStarted,
  newSupplierName,
  newSupplierValue,
  startLines,
  supplierGroups,
  type DeliveryDefaults,
} from '@/components/receiptDelivery';
import {
  ErrorNote,
  Field,
  FormActions,
  FormGroup,
  OkNote,
  Pager,
  StatusPill,
  Table,
  Tile,
  errorMessage,
  lineQuantity,
  money,
  rate,
  quantity,
  shortDate,
  todayIso,
} from '@/components/ui';
import {
  binLabel,
  newIdempotencyKey,
  skuLabel,
  useBinsByStore,
  useLastCosts,
  useSkuCatalog,
  useStockInvalidation,
  useStores,
  useSuppliers,
} from '@/hooks/inventory';
import { awaitWork, retryWork, saveWork, savedWork, takeBack } from '@/offline/engine';
import { announceSavedOffline } from '@/stores/savedOffline';
import { localId, newOperationId } from '@/offline/outbox';
import { piecesOf } from '@/offline/pieces';
import type { NewOperation, Operation } from '@/offline/outbox';
import { confirmAction } from '@/stores/confirm';
import { useFarmScopedStore } from '@/hooks/useFarmScopedStore';
import { useAuth } from '@/stores/auth';
import { farmScopeQuery, useFarmScope } from '@/stores/farmScope';
import type { ScreenIntent } from '@/stores/shellNav';
import { useScreenIntent } from '@/stores/shellNav';
import { packedSuffix } from '@/packing';

const PAGE_SIZE = 50;

interface InwardLineDetail {
  id: string;
  skuId: string;
  skuCode: string;
  skuName: string;
  qty: string;
  unitCost?: string;
  value?: string;
  batchId: string | null;
  batchNo: string | null;
  expiryDate: string | null;
  /** The line's own bin; null follows the receipt's. */
  binId: string | null;
  binCode: string | null;
  binName: string | null;
  supplierId: string;
  supplierCode: string;
  supplier: string;
  storeId: string;
  storeCode: string;
  store: string;
  deliveryDate: string;
  invoiceRef: string | null;
  vehicleNo: string | null;
  remarks: string | null;
}

interface InwardDetail {
  id: string;
  documentNo: string;
  status: string;
  /** Set only when every line shares one supplier or store. */
  supplierId: string | null;
  /** Every supplier on the lines, comma-separated. */
  supplier: string;
  storeId: string | null;
  store: string;
  storeCode: string;
  /** An older receipt's own bin; lines without their own went there. */
  binId: string | null;
  binCode: string | null;
  binName: string | null;
  invoiceRef: string | null;
  /** The earliest line's delivery date … */
  deliveryDate: string;
  /** … and the latest's. */
  deliveryDateTo: string;
  vehicleNo: string | null;
  remarks: string | null;
  totalQty: string;
  totalValue?: string;
  version: number;
  postedAt: string | null;
  reversedAt: string | null;
  reversalReason: string | null;
  lines: InwardLineDetail[];
}

/** Where a line's stock went: its own bin, the receipt's, or loose. */
function lineBin(detail: InwardDetail, line: InwardLineDetail): string {
  if (line.binCode) return `${line.binCode} — ${line.binName}`;
  if (detail.binCode) return `${detail.binCode} — ${detail.binName}`;
  return 'Loose';
}

/** Truthiness, not `!== null`: a server from before bins sends no bin keys at all. */
function usesBins(detail: InwardDetail): boolean {
  return Boolean(detail.binId) || detail.lines.some((l) => Boolean(l.binId));
}

/** "27 Oct 2026", or "25 Oct 2026 – 27 Oct 2026" for lines on several days. */
function dateRange(from: string, to: string | undefined): string {
  return to && to.slice(0, 10) !== from.slice(0, 10) ? `${shortDate(from)} – ${shortDate(to)}` : shortDate(from);
}

/** Every different vehicle or invoice on the lines; an older receipt has only its own. */
function acrossLines(lines: readonly InwardLineDetail[], field: 'vehicleNo' | 'invoiceRef', own: string | null): string {
  return [...new Set(lines.map((l) => l[field]).filter(Boolean))].join(', ') || own || '—';
}

/** "Al-Noor Feeds", or "Al-Noor Feeds +2" with the rest left to a tooltip. */
function firstPlus(values: readonly string[] | undefined, fallback: string): string {
  if (!values || values.length === 0) return fallback;
  return values.length === 1 ? values[0]! : `${values[0]} +${values.length - 1}`;
}

/** The receipt's lines by supplier, in the order each supplier first appears. */
function bySupplier(lines: readonly InwardLineDetail[]): Array<{ supplier: string; lines: InwardLineDetail[] }> {
  const groups = new Map<string, InwardLineDetail[]>();
  for (const line of lines) groups.set(line.supplierId, [...(groups.get(line.supplierId) ?? []), line]);
  return [...groups.values()].map((group) => ({ supplier: group[0]!.supplier, lines: group }));
}

/** The heading over one supplier's lines: who, on which invoices and vehicles. */
function supplierHeading(supplier: string, lines: ReadonlyArray<{ invoiceRef: string | null; vehicleNo: string | null }>): string {
  const invoices = [...new Set(lines.map((l) => l.invoiceRef).filter(Boolean))];
  const vehicles = [...new Set(lines.map((l) => l.vehicleNo).filter(Boolean))];
  return [
    supplier,
    ...(invoices.length ? [`Invoice ${invoices.join(', ')}`] : []),
    ...(vehicles.length ? [`Vehicle ${vehicles.join(', ')}`] : []),
  ].join(' · ');
}

/**
 * The form names a bin on each line only. A receipt (or draft) from when the
 * receipt itself named a bin has that bin given to every line without one of
 * its own — where its stock goes once posted — so nothing moves on save.
 */
function pushReceiptBinDown(lines: ReceiptLine[], binId: string | null | undefined, binLabel = ''): ReceiptLine[] {
  if (!binId) return lines;
  return lines.map((l) => (l.binId || !isStarted(l) ? l : { ...l, binId, binLabel }));
}

/** What a draft saved before every line had its own details kept at the top. */
interface LegacyDraftHeader {
  supplierId?: string;
  storeId?: string;
  binId?: string | null;
  deliveryDate?: string;
  invoiceRef?: string;
  vehicleNo?: string;
  remarks?: string;
}

/**
 * A draft from when supplier, store, invoice, vehicle and remarks were typed
 * once for the whole receipt: each goes onto every line that was started, so
 * the restored form says exactly what the old one did.
 */
/**
 * The date a restored draft was for. One from before the receipt had a single
 * date kept it on its header or on each line, so the earliest of those stands.
 */
function draftDate(saved: {
  deliveryDate?: string;
  header?: LegacyDraftHeader;
  lines: ReadonlyArray<Partial<ReceiptLine> & { deliveryDate?: string }>;
}): string {
  const onLines = saved.lines.map((l) => l.deliveryDate).filter(Boolean).sort();
  return saved.deliveryDate || saved.header?.deliveryDate || onLines[0] || todayIso();
}

function pushHeaderDown(lines: ReceiptLine[], header: LegacyDraftHeader, newName: string | undefined): ReceiptLine[] {
  const supplierId =
    header.supplierId === '__new__' ? (newName?.trim() ? newSupplierValue(newName) : '') : (header.supplierId ?? '');
  const filled = lines.map((l) => {
    if (!isStarted(l)) return l;
    return {
      ...l,
      supplierId: l.supplierId || supplierId,
      supplierLabel: l.supplierLabel || (newSupplierName(supplierId) ?? ''),
      storeId: l.storeId || header.storeId || '',
      invoiceRef: l.invoiceRef || header.invoiceRef || '',
      vehicleNo: l.vehicleNo || header.vehicleNo || '',
      remarks: l.remarks || header.remarks || '',
    };
  });
  return pushReceiptBinDown(filled, header.binId);
}

interface InwardRow {
  id: string;
  documentNo: string;
  status: string;
  deliveryDate: string;
  deliveryDateTo?: string;
  invoiceRef: string | null;
  invoiceRefs?: string[];
  vehicleNo: string | null;
  supplier: string;
  suppliers?: string[];
  store: string;
  storeCode: string;
  storeCodes?: string[];
  lineCount: number;
  totalQty: string;
  totalValue?: string;
}

const INWARD = '/api/v1/inventory/inward';
const LATER = 'Saved on this PC. It will be done when the connection is back.';

/**
 * What a queued receipt carries besides its request: the form as it was
 * typed. It is what the list shows for work not yet sent, and what Fix it
 * loads back (.docs/023).
 */
export interface QueuedReceiptForm {
  deliveryDate: string;
  lines: ReceiptLine[];
}

/**
 * Saves one action on a document through the outbox (.docs/023): answered
 * now with a line, kept on this PC without one. A refusal while the screen is
 * open is taken back out and thrown, for the screen to show.
 */
async function saveOne(op: NewOperation): Promise<'sent' | 'waiting'> {
  const saved = await saveWork([op]);
  // Kept for later: said so where it cannot be missed.
  if (saved.outcome === 'waiting') void announceSavedOffline({ what: op.describes, closeLabel: 'OK' });
  if (saved.outcome === 'sent' || saved.outcome === 'waiting') return saved.outcome;
  for (const o of saved.ops) if (o.state !== 'sent') await takeBack(o.id).catch(() => undefined);
  throw new Error(saved.ops.find((o) => o.state === 'refused')?.lastError ?? UNREACHABLE_MESSAGE);
}

/** A receipt saved on this PC, shown in the list until the server has it. */
function queuedRow(op: Operation, stores: ReadonlyArray<{ id: string; code: string }>): InwardRow {
  const form = op.form as QueuedReceiptForm;
  const lines = form.lines.filter((l) => l.skuId);
  const storeCodes = [...new Set(lines.map((l) => stores.find((s) => s.id === l.storeId)?.code ?? ''))].filter(Boolean);
  const suppliers = [...new Set(lines.map((l) => l.supplierLabel))].filter(Boolean);
  return {
    id: `op:${op.id}`,
    documentNo: 'Not sent yet',
    status: op.state === 'refused' ? 'REFUSED' : 'NOT_SENT',
    deliveryDate: form.deliveryDate,
    invoiceRef: lines[0]?.invoiceRef || null,
    vehicleNo: null,
    supplier: suppliers[0] ?? '',
    suppliers,
    store: '',
    storeCode: storeCodes[0] ?? '',
    storeCodes,
    lineCount: lines.length,
    totalQty: String(lines.reduce((sum, l) => sum + (Number(l.qty) || 0), 0)),
  };
}

/** create: a fresh document, optionally seeded from a past one via "Repeat".
 *  edit: an existing draft, identified and locked to its own id/version.
 *  fix: work the server refused, loaded back to be put right (.docs/023). */
type ComposeState =
  | { mode: 'create'; intent?: ScreenIntent | undefined; seed?: InwardDetail | undefined }
  | { mode: 'edit'; detail: InwardDetail }
  | { mode: 'fix'; op: Operation };

/** One supplier's items, with only the columns that say something about them. */
function SupplierCard({
  detail,
  supplier,
  lines,
  showCost,
}: {
  detail: InwardDetail;
  supplier: string;
  lines: InwardLineDetail[];
  showCost: boolean;
}) {
  const bins = usesBins(detail);
  const severalStores = !detail.storeId;
  const severalDates = dateRange(detail.deliveryDate, detail.deliveryDateTo) !== shortDate(detail.deliveryDate);
  const lots = lines.some((l) => l.batchNo);
  const expiries = lines.some((l) => l.expiryDate);
  const remarks = lines.some((l) => l.remarks);
  // Named once in the heading unless they change from line to line.
  const vehicles = new Set(lines.map((l) => l.vehicleNo ?? '')).size > 1;
  const invoices = new Set(lines.map((l) => l.invoiceRef ?? '')).size > 1;
  return (
    <section className="docpage__card" aria-label={supplier}>
      <div className="docpage__cardhead">
        <strong>{supplierHeading(supplier, lines)}</strong>
        {showCost && <strong>{money(lines.reduce((sum, l) => sum + Number(l.value ?? 0), 0).toFixed(2))}</strong>}
      </div>
      <Table
        count={lines.length}
        empty="No items."
        head={
          <tr>
            <th>Item</th>
            <th className="num">Quantity</th>
            {showCost && <th className="num">Unit cost</th>}
            {showCost && <th className="num">Value</th>}
            {lots && <th>Lot</th>}
            {expiries && <th>Expiry</th>}
            {severalStores && <th>Store</th>}
            {bins && <th>Bin</th>}
            {severalDates && <th>Date</th>}
            {vehicles && <th>Vehicle</th>}
            {invoices && <th>Invoice</th>}
            {remarks && <th>Remarks</th>}
          </tr>
        }
      >
        {lines.map((l) => (
          <tr key={l.id}>
            <td>
              <span className="mono">{l.skuCode}</span> {l.skuName}
            </td>
            <td className="num">{lineQuantity(l.qty)}</td>
            {showCost && <td className="num">{rate(l.unitCost)}</td>}
            {showCost && <td className="num">{money(l.value)}</td>}
            {lots && <td className="dim">{l.batchNo ?? '—'}</td>}
            {expiries && <td className="dim">{l.expiryDate ? shortDate(l.expiryDate) : '—'}</td>}
            {severalStores && <td className="dim mono">{l.storeCode}</td>}
            {bins && <td className="dim">{lineBin(detail, l)}</td>}
            {severalDates && <td className="dim">{shortDate(l.deliveryDate)}</td>}
            {vehicles && <td className="dim mono">{l.vehicleNo ?? '—'}</td>}
            {invoices && <td className="dim mono">{l.invoiceRef ?? '—'}</td>}
            {remarks && <td className="dim">{l.remarks ?? '—'}</td>}
          </tr>
        ))}
      </Table>
    </section>
  );
}

/** A receipt's own screen: what came in, from whom, where it went, and what has happened to it since. */
function InwardDocument({ detail, showCost }: { detail: InwardDetail; showCost: boolean }) {
  const groups = bySupplier(detail.lines);
  return (
    <>
      {detail.reversedAt && (
        <p className="docpage__note docpage__note--alert" role="note">
          Reversed on {shortDate(detail.reversedAt)}. Reason: {detail.reversalReason ?? '—'}
        </p>
      )}
      {detail.status === 'DRAFT' && (
        <p className="docpage__note" role="note">
          This is a draft. None of it has reached stock yet.
        </p>
      )}

      <div className="tiles">
        <Tile label="Items" value={String(detail.lines.length)} />
        <Tile label="Quantity" value={quantity(detail.totalQty, 2)} />
        {showCost && <Tile label="Value" value={money(detail.totalValue)} />}
        <Tile label={groups.length === 1 ? 'Supplier' : 'Suppliers'} value={String(groups.length)} />
      </div>

      <section className="stack docpage__section">
        <h3 className="formsection">Delivery</h3>
        <dl className="detaillist">
          <dt>{detail.storeId ? 'Store' : 'Stores'}</dt>
          <dd>{detail.storeId ? `${detail.storeCode} — ${detail.store}` : detail.storeCode}</dd>
          {/* Receipts now name a bin per line; only an older one has its own. */}
          {detail.binCode && (
            <>
              <dt>Bin</dt>
              <dd>
                {detail.binCode} — {detail.binName}
              </dd>
            </>
          )}
          <dt>Delivery date</dt>
          <dd>{dateRange(detail.deliveryDate, detail.deliveryDateTo)}</dd>
          <dt>Vehicle</dt>
          <dd>{acrossLines(detail.lines, 'vehicleNo', detail.vehicleNo)}</dd>
          <dt>Invoice</dt>
          <dd>{acrossLines(detail.lines, 'invoiceRef', detail.invoiceRef)}</dd>
          {detail.remarks && (
            <>
              <dt>Remarks</dt>
              <dd>{detail.remarks}</dd>
            </>
          )}
          {detail.postedAt && (
            <>
              <dt>Posted on</dt>
              <dd>{shortDate(detail.postedAt)}</dd>
            </>
          )}
        </dl>
      </section>

      <section className="stack docpage__section">
        <h3 className="formsection">Items received</h3>
        {groups.map((group) => (
          <SupplierCard
            key={group.lines[0]!.id}
            detail={detail}
            // Two suppliers can share a name; only the code tells them apart.
            supplier={
              groups.filter((g) => g.supplier === group.supplier).length > 1
                ? `${group.supplier} (${group.lines[0]!.supplierCode})`
                : group.supplier || detail.supplier
            }
            lines={group.lines}
            showCost={showCost}
          />
        ))}
      </section>

      <section className="stack docpage__section">
        <h3 className="formsection">History</h3>
        <HistoryPanel path={`/api/v1/inventory/inward/${detail.id}/history`} />
      </section>
    </>
  );
}

export function InwardShipments() {
  const intent = useScreenIntent('inv-inward');
  const { can } = useAuth();
  const invalidate = useStockInvalidation();
  const farmIds = useFarmScope((s) => s.selectedFarmIds);
  const showCost = can('inv.price.read');
  const [compose, setCompose] = useState<ComposeState | null>(() =>
    intent?.action === 'create' && can('inv.inward.create') ? { mode: 'create', intent } : null,
  );
  const [skip, setSkip] = useState(0);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  // Sent here to open one receipt: from Offline work, once it has gone.
  const [openKey, setOpenKey] = useState<string | null>(() => intent?.recordId ?? null);
  const [note, setNote] = useState<string | null>(null);
  const [repeatBusy, setRepeatBusy] = useState(false);
  const { data: allStores } = useStores();

  // Work saved on this PC and not yet with the server leads the list
  // (.docs/023): what somebody is still waiting on, with no number yet.
  const outbox = useSavedWork();
  const queued = (outbox.data ?? [])
    .filter((op) => op.kind === 'inward.create' && op.state !== 'sent' && op.form !== undefined)
    .reverse();
  const opFor = (row: InwardRow) => queued.find((op) => `op:${op.id}` === row.id);

  // Sent here from Offline work to put refused work right.
  useEffect(() => {
    const opId = intent?.fixOperation;
    if (!opId) return;
    void savedWork().then((ops) => {
      const op = ops.find((o) => o.id === opId);
      if (op?.form) setCompose({ mode: 'fix', op });
    });
  }, [intent?.fixOperation]);

  async function throwAway(op: Operation) {
    const ok = await confirmAction({
      title: 'Throw this work away?',
      message: `"${op.describes}" will be removed from this PC. The server will never see it.`,
      confirmLabel: 'Throw it away',
      cancelLabel: 'Keep it',
      danger: true,
    });
    if (!ok) return;
    await takeBack(op.id);
    setSelectedKey(null);
    setNote('Thrown away.');
  }

  const list = useQuery({
    queryKey: ['inv', 'inward', farmIds, skip],
    queryFn: () => api.get('/api/v1/inventory/inward', { take: PAGE_SIZE, skip, ...farmScopeQuery(farmIds) }),
  });
  const { data, isPending, error } = list;

  const detailQuery = useQuery({
    queryKey: ['inv', 'inward', 'detail', openKey],
    queryFn: async () =>
      (await api.get(`/api/v1/inventory/inward/${openKey}` as '/api/v1/inventory/inward/{id}')) as unknown as InwardDetail,
    // A row saved on this PC has no receipt on the server yet.
    enabled: openKey !== null && !openKey.startsWith('op:'),
  });
  const { data: detail, error: detailError, isFetching: detailFetching } = detailQuery;

  // Each goes through the outbox, carrying the version that was seen where the
  // server takes one (.docs/023).
  const post = useMutation({
    mutationFn: (doc: { id: string; documentNo: string; version?: number | undefined }) =>
      saveOne({
        kind: 'inward.post',
        method: 'POST',
        path: `${INWARD}/${doc.id}/post`,
        ...(doc.version ? { body: { version: doc.version } } : {}),
        describes: `Posting ${doc.documentNo}`,
        screen: 'inv-inward',
      }),
    onSuccess: (outcome) => { invalidate(); setNote(outcome === 'sent' ? 'Posted to the ledger.' : LATER); },
  });
  const del = useMutation({
    mutationFn: ({ id, version, documentNo }: { id: string; version: number; documentNo: string }) =>
      saveOne({
        kind: 'inward.cancel',
        method: 'DELETE',
        path: `${INWARD}/${id}?version=${version}`,
        describes: `Deleting draft ${documentNo}`,
        screen: 'inv-inward',
      }),
    onSuccess: (outcome) => { invalidate(); setOpenKey(null); setSelectedKey(null); setNote(outcome === 'sent' ? 'Draft deleted.' : LATER); },
  });
  const reverse = useMutation({
    mutationFn: ({ id, reason, documentNo }: { id: string; reason: string; documentNo: string }) =>
      saveOne({
        kind: 'inward.reverse',
        method: 'POST',
        path: `${INWARD}/${id}/reverse`,
        body: { reason },
        describes: `Reversing ${documentNo}`,
        screen: 'inv-inward',
      }),
    onSuccess: (outcome) => { invalidate(); setNote(outcome === 'sent' ? 'Reversed.' : LATER); },
  });

  const rows = skip === 0
    ? [...queued.map((op) => queuedRow(op, allStores ?? [])), ...((data?.items ?? []) as InwardRow[])]
    : ((data?.items ?? []) as InwardRow[]);

  /** Moving between the list and a receipt: what was said on one is not news on the other. */
  function show(key: string | null) {
    setOpenKey(key);
    setNote(null);
    post.reset();
    del.reset();
    reverse.reset();
  }

  /** The whole receipt: from the server, or as this PC last loaded it. */
  async function fetchFull(id: string): Promise<InwardDetail> {
    return fetchOrCached(
      ['inv', 'inward', 'detail', id],
      async () => (await api.get(`/api/v1/inventory/inward/${id}` as '/api/v1/inventory/inward/{id}')) as unknown as InwardDetail,
      'This receipt',
    );
  }

  async function repeat(row: InwardRow) {
    setRepeatBusy(true);
    try {
      setCompose({ mode: 'create', seed: await fetchFull(row.id) });
    } catch (e) {
      setNote(errorMessage(e, 'Could not load that shipment to repeat it'));
    } finally {
      setRepeatBusy(false);
    }
  }

  async function print(row: InwardRow) {
    try {
      printShipment(await fetchFull(row.id));
    } catch (e) {
      setNote(errorMessage(e, 'Could not load that shipment to print it'));
    }
  }

  function printShipment(full: InwardDetail) {
    const severalStores = !full.storeId;
    printDocument({
      title: `Inward shipment ${full.documentNo}`,
      facts: [
        ['Status', full.status],
        ['Supplier', full.supplier],
        ['Store', full.storeId ? `${full.storeCode} — ${full.store}` : full.storeCode],
        ...(full.binCode ? ([['Bin', `${full.binCode} — ${full.binName}`]] as Array<[string, string]>) : []),
        ['Delivery date', dateRange(full.deliveryDate, full.deliveryDateTo)],
        ['Invoice', full.invoiceRef ?? '—'],
        ['Vehicle', full.vehicleNo ?? '—'],
        ['Total quantity', quantity(full.totalQty, 2)],
        ...(showCost ? ([['Total value', money(full.totalValue)]] as Array<[string, string]>) : []),
      ],
      lines: bySupplier(full.lines).flatMap((group) =>
        group.lines.map((l) => ({
          item: [
            `${l.skuCode} — ${l.skuName}`,
            ...(severalStores ? [l.storeCode] : []),
            ...(usesBins(full) ? [lineBin(full, l)] : []),
            ...(full.deliveryDateTo && full.deliveryDateTo.slice(0, 10) !== full.deliveryDate.slice(0, 10)
              ? [shortDate(l.deliveryDate)]
              : []),
            ...(l.remarks ? [l.remarks] : []),
          ].join(' · '),
          qty: quantity(l.qty, 4),
          ...(l.unitCost ? { unitCost: rate(l.unitCost) } : {}),
          ...(l.value ? { value: money(l.value), amount: l.value } : {}),
          ...(l.batchNo ? { lot: l.batchNo } : {}),
          group: supplierHeading(group.supplier, group.lines),
        })),
      ),
      showCost,
    });
  }

  async function edit(row: InwardRow) {
    try {
      setCompose({ mode: 'edit', detail: await fetchFull(row.id) });
    } catch (e) {
      setNote(errorMessage(e, 'Could not load that shipment to edit it'));
    }
  }

  function rowActions(row: InwardRow): DesktopAction[] {
    const op = opFor(row);
    if (op) {
      return [
        { label: 'Open', icon: 'details' as const, shortcut: 'Enter', onSelect: () => show(row.id), allowed: true },
        {
          label: 'Fix it', icon: 'edit' as const,
          onSelect: () => setCompose({ mode: 'fix', op }),
          allowed: can('inv.inward.create'),
          enabled: op.state === 'refused',
          reason: 'Only work the server refused needs fixing',
        },
        {
          label: 'Throw it away', icon: 'remove' as const,
          onSelect: () => void throwAway(op),
          allowed: true,
          enabled: op.state === 'refused',
          reason: 'Work still waiting to be sent cannot be thrown away',
        },
      ];
    }
    return [
      { label: 'Open', icon: 'details' as const, shortcut: 'Enter', onSelect: () => show(row.id), allowed: true },
      {
        label: 'Edit', icon: 'edit' as const,
        onSelect: () => void edit(row),
        allowed: can('inv.inward.update'),
        enabled: row.status === 'DRAFT',
        reason: 'Only a draft can be edited',
      },
      {
        label: 'Post', icon: 'post' as const,
        onSelect: () => post.mutate({ id: row.id, documentNo: row.documentNo, version: detail?.id === row.id ? detail.version : undefined }),
        allowed: can('inv.inward.post'),
        enabled: row.status === 'DRAFT',
        reason: 'Only a draft can be posted',
      },
      { label: 'Repeat', icon: 'repeat' as const, onSelect: () => void repeat(row), allowed: can('inv.inward.create'), enabled: !repeatBusy, separatorBefore: true },
      { label: 'Print', icon: 'print' as const, onSelect: () => void print(row), allowed: true },
    ];
  }

  const toolbarActions: DesktopAction[] = [
    { label: 'Receive Stock', icon: 'inbound', primary: true, onSelect: () => setCompose({ mode: 'create' }), allowed: can('inv.inward.create') },
  ];

  const columns: Array<GridColumn<InwardRow>> = [
    { key: 'doc', header: 'Document', width: 150, render: (r) => <span className="withicon"><AppIcon kind="inbound" size={14} /><span className="mono">{r.documentNo}</span></span> },
    { key: 'date', header: 'Date', width: 100, render: (r) => dateRange(r.deliveryDate, r.deliveryDateTo) },
    { key: 'store', header: 'Store', width: 100, render: (r) => <span className="dim" title={r.storeCode}>{firstPlus(r.storeCodes, r.storeCode)}</span> },
    { key: 'invoice', header: 'Invoice', width: 120, render: (r) => <span className="dim" title={r.invoiceRef ?? undefined}>{firstPlus(r.invoiceRefs, r.invoiceRef ?? '—')}</span> },
    { key: 'lines', header: 'Lines', align: 'end', width: 70, render: (r) => r.lineCount },
    { key: 'qty', header: 'Quantity', align: 'end', width: 100, render: (r) => quantity(r.totalQty, 2) },
    ...(showCost ? [{ key: 'value', header: 'Value', align: 'end' as const, width: 120, render: (r: InwardRow) => money(r.totalValue) }] : []),
    { key: 'status', header: 'Status', width: 110, render: (r) => <StatusPill status={r.status} /> },
  ];

  if (compose) {
    return (
      <InwardCompose
        state={compose}
        onCancel={() => setCompose(null)}
        onSaved={(message) => { setCompose(null); setNote(message); }}
      />
    );
  }

  // Work saved on this PC: shown as it was typed until the server has it.
  const savedPiece = openKey?.startsWith('op:')
    ? piecesOf(outbox.data ?? []).find((p) => p.ops.some((op) => `op:${op.id}` === openKey))
    : undefined;
  if (savedPiece) {
    return (
      <SavedWorkDetail
        piece={savedPiece}
        all={outbox.data ?? []}
        backLabel="Back to Stock IN"
        onBack={() => show(null)}
        onFix={(op) => { show(null); setCompose({ mode: 'fix', op }); }}
        onOpenDocument={(id) => show(id)}
        onDone={(said) => { show(null); setNote(said); }}
      />
    );
  }

  if (openKey && !openKey.startsWith('op:')) {
    const openRow = rows.find((r) => r.id === openKey);
    const canEdit = detail?.status === 'DRAFT' && can('inv.inward.update');
    const canDelete = detail?.status === 'DRAFT' && can('inv.inward.delete');
    const canPost = detail?.status === 'DRAFT' && can('inv.inward.post');
    const canReverse = detail?.status === 'POSTED' && can('inv.inward.reverse');
    return (
      <DocumentFrame
        onBack={() => show(null)}
        backLabel="Back to Stock IN"
        title={
          detail && (
            <>
              <h2 className="withicon">
                <AppIcon kind="inbound" size={18} />
                <span className="mono">{detail.documentNo}</span>
              </h2>
              <StatusPill status={detail.status} />
              <span className="muted">{dateRange(detail.deliveryDate, detail.deliveryDateTo)}</span>
            </>
          )
        }
        actions={
          detail && (
            <>
              <button type="button" className="btn btn--withicon" onClick={() => printShipment(detail)}>
                <AppIcon kind="print" size={15} />
                Print
              </button>
              {can('inv.inward.create') && (
                <button
                  type="button"
                  className="btn btn--withicon"
                  onClick={() => {
                    // Leaves this receipt behind: "Back to list" means the list.
                    show(null);
                    setCompose({ mode: 'create', seed: detail });
                  }}
                >
                  <AppIcon kind="repeat" size={15} />
                  Repeat
                </button>
              )}
              {canDelete && (
                <DeleteButton pending={del.isPending} onDelete={() => del.mutate({ id: detail.id, version: detail.version, documentNo: detail.documentNo })} />
              )}
              {canReverse && (
                <ReverseButton pending={reverse.isPending} onReverse={(reason) => reverse.mutate({ id: detail.id, reason, documentNo: detail.documentNo })} />
              )}
              {canEdit && (
                <button type="button" className="btn btn--withicon" onClick={() => setCompose({ mode: 'edit', detail })}>
                  <AppIcon kind="edit" size={15} />
                  Edit
                </button>
              )}
              {canPost && (
                <button
                  type="button"
                  className="btn btn--primary btn--withicon"
                  disabled={post.isPending || detailFetching}
                  onClick={() => post.mutate({ id: detail.id, documentNo: detail.documentNo, version: detail.version })}
                >
                  <AppIcon kind="post" size={15} />
                  {post.isPending ? 'Posting…' : 'Post'}
                </button>
              )}
            </>
          )
        }
      >
        {note && <OkNote message={note} />}
        {(post.error ?? del.error ?? reverse.error) && (
          <ErrorNote message={errorMessage(post.error ?? del.error ?? reverse.error, 'Action failed')} />
        )}
        {detailError && !detail && <ErrorNote message={detailError.message} />}
        {!detail && isOfflineGap(detailQuery) && openRow && <OfflineDetails row={openRow} columns={columns} />}
        {!detail && !detailError && !(isOfflineGap(detailQuery) && openRow) && <LoadingNote what="Loading the receipt…" />}
        {detail && <InwardDocument detail={detail} showCost={showCost} />}
      </DocumentFrame>
    );
  }

  return (
    <div className="workbench">
      <div className="workbench__main">
        <ActionBar actions={toolbarActions} />

        {note && <OkNote message={note} />}
        {(post.error ?? del.error ?? reverse.error) && (
          <ErrorNote message={errorMessage(post.error ?? del.error ?? reverse.error, 'Action failed')} />
        )}
        {error && <ErrorNote message={error.message} />}

        <DataGrid
          id="documents-inward"
          label="Inward shipments"
          columns={columns}
          rows={rows}
          rowKey={(r) => r.id}
          selectedKey={selectedKey}
          onSelect={setSelectedKey}
          // A row saved on this PC opens as what was saved, and where it has got to.
          onOpen={(row) => show(row.id)}
          openOnClick
          actions={rowActions}
          loading={isPending && !isOfflineGap(list)}
          empty={isOfflineGap(list) ? NOT_ON_THIS_PC : 'No shipments received yet.'}
        />
        <Pager skip={skip} take={PAGE_SIZE} total={data?.total ?? 0} onChange={setSkip} />
      </div>
    </div>
  );
}

function InwardCompose({
  state,
  onCancel,
  onSaved,
}: {
  state: ComposeState;
  onCancel: () => void;
  onSaved: (message: string) => void;
}) {
  const { can } = useAuth();
  const invalidate = useStockInvalidation();
  const queryClient = useQueryClient();
  const { data: stores } = useStores();
  const { data: suppliers } = useSuppliers();
  const catalog = useSkuCatalog();

  const initial = state.mode === 'edit' ? state.detail : undefined;
  const seed = initial ?? (state.mode === 'create' ? state.seed : undefined);
  const intent = state.mode === 'create' ? state.intent : undefined;
  const editing = initial !== undefined;
  // Refused work being put right: its form comes back as it was typed.
  const fixing = state.mode === 'fix' ? state.op : undefined;
  const fixForm = fixing?.form as QueuedReceiptForm | undefined;
  const canPost = can('inv.inward.post');
  const canSeeCost = can('inv.price.read');
  const linesLocked = editing && !canSeeCost;
  const showCost = editing ? canSeeCost : true;

  // The store a first line starts with, which has no line above to copy: the
  // one the screen was opened for, or the farm picked in the top bar's.
  const [defaultStoreId, setDefaultStoreId] = useState(intent?.storeId ?? '');
  const { options: storeOptions } = useFarmScopedStore(stores, defaultStoreId, setDefaultStoreId);
  const defaults: DeliveryDefaults = {
    storeId: defaultStoreId || (storeOptions.length === 1 ? storeOptions[0]!.id : ''),
  };

  // One date for the whole receipt. An older one can hold several: it opens on
  // the earliest, and saving puts every line on this one.
  const [deliveryDate, setDeliveryDate] = useState(
    () => fixForm?.deliveryDate ?? initial?.deliveryDate.slice(0, 10) ?? todayIso(),
  );
  const severalSavedDates =
    initial?.deliveryDateTo !== undefined && initial.deliveryDateTo.slice(0, 10) !== initial.deliveryDate.slice(0, 10);

  // The form opens with a screenful of blank rows, so a paper invoice is typed
  // straight down the grid instead of one "+ Add item" per line. Lines shown
  // read-only are left as saved: blank rows nobody can fill are only noise.
  const [lines, setLines] = useState<ReceiptLine[]>(() => {
    if (fixForm) return fixForm.lines.map(({ key: _old, ...rest }) => emptyReceiptLine(rest));
    const seeded = seed
      ? seed.lines.map((l) =>
          emptyReceiptLine({
            lineId: initial ? l.id : '',
            skuId: l.skuId,
            skuLabel: `${l.skuCode} — ${l.skuName}`,
            qty: l.qty,
            unitCost: initial ? (l.unitCost ?? '') : '',
            batchNo: initial ? (l.batchNo ?? '') : '',
            expiryDate: initial ? (l.expiryDate?.slice(0, 10) ?? '') : '',
            binId: l.binId ?? '',
            binLabel: l.binCode ? `${l.binCode} — ${l.binName}` : '',
            // Repeat brings back who supplied what, where it went and on
            // which vehicle; a new delivery has its own invoice.
            // A line from before details moved onto lines has none of its
            // own: the receipt's are what it had.
            supplierId: l.supplierId ?? seed.supplierId ?? '',
            supplierLabel: l.supplier ?? seed.supplier ?? '',
            storeId: l.storeId ?? seed.storeId ?? '',
            invoiceRef: initial ? (l.invoiceRef ?? seed.invoiceRef ?? '') : '',
            vehicleNo: l.vehicleNo ?? seed.vehicleNo ?? '',
            remarks: initial ? (l.remarks ?? seed.remarks ?? '') : '',
            // As saved: an empty box here was left empty on purpose.
            carried: true,
          }),
        )
      : startLines([], [emptyReceiptLine({ skuId: intent?.skuId ?? '' })], {
          storeId: intent?.storeId ?? '',
        });
    const placed = seed?.binCode
      ? pushReceiptBinDown(seeded, seed.binId, `${seed.binCode} — ${seed.binName}`)
      : seeded;
    return linesLocked ? placed : padRows(placed, emptyReceiptLine);
  });
  const [problem, setProblem] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [reviewing, setReviewing] = useState(false);
  const idempotencyKey = useRef(newIdempotencyKey('inw'));

  /** Every change to the lines goes through here, so a line that has just
   *  been started takes its supplier, store, date… from the line above. */
  function changeLines(next: ReceiptLine[]) {
    setLines((prev) => startLines(prev, next, defaults));
  }

  // A first line started before the stores loaded gets its store once the
  // default is known. Only an empty box is filled.
  useEffect(() => {
    if (!defaults.storeId) return;
    setLines((prev) => {
      const at = prev.findIndex(isStarted);
      if (at < 0 || prev[at]!.storeId) return prev;
      return prev.map((l, i) => (i === at ? { ...l, storeId: defaults.storeId } : l));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaults.storeId]);

  // Ten minutes of typing survives a reload, a crash or a closed window.
  // Editing one document must not offer another one's typing, so the draft is
  // named after what it is editing.
  const emptyLines = () => padRows([emptyReceiptLine({ skuId: intent?.skuId ?? '' })], emptyReceiptLine);
  const draft = useDraft(
    editing ? `inward.edit.${initial.id}` : 'inward.new',
    { lines, deliveryDate, idempotencyKey: idempotencyKey.current },
    (saved) => {
      // Row keys come from a counter that restarts with the page, so restored
      // rows take fresh ones rather than colliding with a later form's.
      // A draft from before lines remembered being filled counts its started
      // lines as filled, so nothing in them is overwritten. Such a draft has
      // no `carried` on its lines at all, whatever the stored type says, so
      // the check is for the field being absent rather than false — a line
      // deliberately saved as not carried must stay that way.
      const restored = saved.lines.map(({ key: _old, ...rest }) => {
        const line = emptyReceiptLine(rest);
        return (rest as Partial<ReceiptLine>).carried === undefined
          ? { ...line, carried: isStarted(line) }
          : line;
      });
      // A draft from when the receipt itself named one supplier, store, date…
      // for every line: those move onto its lines.
      const legacy = saved as typeof saved & { header?: LegacyDraftHeader; newSupplierName?: string };
      setLines(
        padRows(legacy.header ? pushHeaderDown(restored, legacy.header, legacy.newSupplierName) : restored, emptyReceiptLine),
      );
      setDeliveryDate(draftDate(legacy));
      // The same key as the attempt that was interrupted: if that one actually
      // reached the server and only the reply was lost, reusing it is what
      // stops a second shipment being created.
      idempotencyKey.current = saved.idempotencyKey;
    },
  );

  function startAgain() {
    setLines(emptyLines());
    setDeliveryDate(todayIso());
    setProblem(null);
    idempotencyKey.current = newIdempotencyKey('inw');
  }

  const { loadByIds } = catalog;
  useEffect(() => {
    const ids = lines.map((l) => l.skuId).filter(Boolean);
    if (ids.length === 0) return;
    void loadByIds(ids).then((rows) => {
      setLines((prev) =>
        prev.map((l) => {
          if (l.skuLabel || !l.skuId) return l;
          const sku = rows.find((r) => r.id === l.skuId);
          return sku ? { ...l, skuLabel: skuLabel(sku) } : l;
        }),
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startedLines = lines.filter(isStarted);

  // Stores a line may receive into: those of the farms in the top bar, plus
  // any a line already names (an edit, a repeat) so it still shows.
  const activeStores = (stores ?? []).filter((s) => s.isActive !== false);
  const stripStores = [
    ...storeOptions.filter((s) => s.isActive !== false),
    ...activeStores.filter((s) => !storeOptions.some((o) => o.id === s.id) && lines.some((l) => l.storeId === s.id)),
  ];
  const binsByStore = useBinsByStore(lines.filter(isStarted).map((l) => l.storeId));

  const lastCosts = useLastCosts(
    lines.map((l) => l.skuId),
    !editing,
    startedLines.map((l) => ({ skuId: l.skuId, supplierId: l.supplierId })),
  );
  const lastCostsById = new Map(lastCosts.data?.map((c) => [c.skuId, c]));

  const supplierName = (l: ReceiptLine) =>
    newSupplierName(l.supplierId) ?? (suppliers?.find((s) => s.id === l.supplierId)?.nameEn || l.supplierLabel);
  const storeOf = (l: ReceiptLine) => activeStores.find((s) => s.id === l.storeId);

  function validate(): string | null {
    if (!linesLocked && startedLines.length === 0) return 'Add at least one item.';
    if (!deliveryDate) return 'Choose the delivery date.';
    for (const [i, l] of lines.entries()) {
      if (!isStarted(l) && !l.lineId) continue;
      const n = i + 1;
      if (!linesLocked) {
        if (!l.skuId) return `Line ${n} has a quantity but no item.`;
        if (!(Number(l.qty) > 0)) return `Line ${n} needs a quantity above zero.`;
        if (catalog.known.get(l.skuId)?.hasExpiry && !l.expiryDate)
          return `Line ${n} is tracked by expiry, so it needs an expiry date.`;
      }
      if (!l.supplierId) return `Line ${n} needs a supplier.`;
      if (!l.storeId) return `Line ${n} needs a store to receive into.`;
      if (stores && !storeOf(l)) return `Line ${n}'s store is closed or not one of yours. Choose another store.`;
      // Deleted since the draft or receipt was saved; the server would refuse it.
      const bins = binsByStore.get(l.storeId);
      if (l.binId && bins && !bins.some((b) => b.id === l.binId)) {
        const name = l.binLabel.split(' — ')[0] || 'its bin';
        return `Line ${n}'s bin ${name} has been deleted. Choose another bin, or clear it to leave the stock loose.`;
      }
    }
    return null;
  }

  /** Worth a second look, but not wrong: shown in the review, never blocking. */
  function warnings(): string[] {
    const out: string[] = [];
    for (const [i, l] of lines.entries()) {
      if (!isStarted(l)) continue;
      if (l.expiryDate && deliveryDate && l.expiryDate <= deliveryDate)
        out.push(`Line ${i + 1} expires on or before the day it was delivered.`);
      if (showCost && !linesLocked && l.skuId && !l.unitCost.trim())
        out.push(`Line ${i + 1} has no cost, so it will be received at zero.`);
    }
    return out;
  }

  const save = useMutation({
    mutationFn: async (andPost: boolean) => {
      // Each supplier typed on a line is added once, however many lines name
      // it, in the same batch as the receipt and ahead of it (.docs/023): with
      // a line it is made at once; without, it waits on this PC with the
      // receipt, which is sent with the supplier's new id in its place.
      const registered = new Map<string, string>();
      const supplierOps: NewOperation[] = [];
      for (const value of new Set(lines.map((l) => l.supplierId).filter((id) => newSupplierName(id) !== null))) {
        if (!lines.some((l) => l.supplierId === value && (isStarted(l) || l.lineId))) continue;
        const id = newOperationId();
        const name = newSupplierName(value)!;
        supplierOps.push({ id, kind: 'supplier.create', method: 'POST', path: '/api/v1/suppliers', body: { nameEn: name }, describes: `New supplier: ${name}`, screen: 'inv-reference' });
        registered.set(value, localId(id));
      }
      /** Lines naming a supplier the server has now made switch to it, so saving again cannot add it twice. */
      const settleSuppliers = (ops: readonly Operation[]) => {
        const made = new Map<string, string>();
        for (const [value, local] of registered) {
          const op = ops.find((o) => localId(o.id) === local);
          if (op?.state === 'sent' && op.resultId) made.set(value, op.resultId);
        }
        if (!made.size) return;
        setLines((prev) => prev.map((l) => (made.has(l.supplierId) ? { ...l, supplierId: made.get(l.supplierId)! } : l)));
        void queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      };
      const supplierOf = (l: ReceiptLine) => registered.get(l.supplierId) ?? l.supplierId;

      const delivery = (l: ReceiptLine) => ({
        supplierId: supplierOf(l),
        storeId: l.storeId,
        deliveryDate,
        ...(l.invoiceRef.trim() ? { invoiceRef: l.invoiceRef.trim() } : {}),
        ...(l.vehicleNo.trim() ? { vehicleNo: l.vehicleNo.trim() } : {}),
        ...(l.remarks.trim() ? { remarks: l.remarks.trim() } : {}),
      });
      const payloadLines = startedLines.map((l) => ({
        skuId: l.skuId,
        qty: l.qty,
        unitCost: l.unitCost || '0',
        ...(l.batchNo.trim() ? { batchNo: l.batchNo.trim() } : {}),
        ...(l.expiryDate ? { expiryDate: l.expiryDate } : {}),
        ...(l.binId ? { binId: l.binId } : {}),
        ...delivery(l),
      }));

      const form = { deliveryDate, lines: startedLines } satisfies QueuedReceiptForm;
      const describes = `Stock IN ${shortDate(deliveryDate)}: ${startedLines.length} line(s)`;

      if (fixing) {
        // A new supplier goes on its own; the receipt waits for it.
        if (supplierOps.length) settleSuppliers((await saveWork(supplierOps)).ops);
        // Sent again under a new key, with its post following it.
        const newId = await retryWork(fixing.id, { body: { lines: payloadLines }, form, describes });
        const saved = newId ? await awaitWork([newId]) : null;
        if (!saved || saved.outcome === 'refused' || saved.outcome === 'not-kept') {
          throw new Error(saved?.ops[0]?.lastError ?? 'The server still refused it. It is kept under Offline work, Needs attention.');
        }
        const made = saved.ops[0]?.result as { documentNo?: string } | undefined;
        return { documentNo: made?.documentNo ?? '', posted: false, waiting: saved.outcome === 'waiting', warning: undefined };
      }

      // Through the outbox (.docs/023): answered now with a line, kept on this
      // PC without one. A change carries the version that was seen; the post
      // after it carries the version the change makes.
      const batch: NewOperation[] = initial
        ? [
            {
              kind: 'inward.edit',
              method: 'PATCH',
              path: `${INWARD}/${initial.id}`,
              body: {
                version: initial.version,
                ...(linesLocked
                  ? {
                      // Costs are hidden for this user, so item, quantity and cost
                      // stay as saved; only each line's delivery details change.
                      lineDetails: lines
                        .filter((l) => l.lineId)
                        .map((l) => ({
                          id: l.lineId,
                          binId: l.binId,
                          invoiceRef: l.invoiceRef.trim(),
                          vehicleNo: l.vehicleNo.trim(),
                          remarks: l.remarks.trim(),
                          supplierId: supplierOf(l),
                          storeId: l.storeId,
                          deliveryDate,
                        })),
                    }
                  : {
                      // An older receipt's own bin now sits on its lines, so a
                      // line left loose here is loose.
                      binId: '',
                      lines: payloadLines,
                    }),
              },
              describes: `Changing ${initial.documentNo}`,
              screen: 'inv-inward',
            },
          ]
        : [{ kind: 'inward.create', method: 'POST', path: INWARD, body: { lines: payloadLines }, describes, screen: 'inv-inward', form }];
      if (andPost) {
        batch.push(
          initial
            ? { kind: 'inward.post', method: 'POST', path: `${INWARD}/${initial.id}/post`, body: { version: initial.version + 1 }, describes: `Posting ${initial.documentNo}`, screen: 'inv-inward' }
            : { kind: 'inward.post', method: 'POST', path: `${INWARD}/{dep}/post`, after: supplierOps.length, describes: `Posting the ${describes}`, screen: 'inv-inward' },
        );
      }

      const saved = await saveWork([...supplierOps, ...batch]);
      settleSuppliers(saved.ops);
      // The receipt's own operations, after any supplier added with it.
      const docOps = saved.ops.slice(supplierOps.length);
      const made = (initial ? { documentNo: initial.documentNo } : docOps[0]?.result) as { documentNo?: string } | undefined;
      const documentNo = made?.documentNo ?? '';
      if (saved.outcome === 'sent') return { documentNo, posted: andPost, waiting: false, warning: undefined };
      if (saved.outcome === 'waiting') return { documentNo, posted: andPost, waiting: true, warning: undefined };

      // Refused while this form is open: said here rather than queued up for
      // Needs attention too. What the server took stays.
      for (const op of saved.ops) if (op.state !== 'sent') await takeBack(op.id).catch(() => undefined);
      const reason = saved.ops.find((op) => op.state === 'refused')?.lastError ?? UNREACHABLE_MESSAGE;
      if (!initial && docOps[0]?.state === 'sent') {
        // The receipt was made and only its post was refused: the form must
        // not make another. Post the draft from the list.
        return { documentNo, posted: false, waiting: false, warning: `${documentNo} was saved as a draft but not posted: ${reason}. Post it from the list.` };
      }
      throw new Error(reason);
    },
    onSuccess: (result) => {
      invalidate();
      const text =
        result.warning ??
        (result.waiting
          ? LATER
          : result.posted
            ? `${result.documentNo} received and posted to stock.`
            : editing
              ? `${result.documentNo} saved.`
              : `${result.documentNo} saved as a draft.${canPost ? '' : ' Someone with posting rights must commit it.'}`);

      draft.forget();
      const what = editing
        ? `The change to ${initial.documentNo}`
        : `Stock IN ${shortDate(deliveryDate)}: ${startedLines.length} ${startedLines.length === 1 ? 'item' : 'items'}`;

      if (editing || fixing) {
        onSaved(text);
        if (result.waiting) void announceSavedOffline({ what, closeLabel: 'OK' });
        return;
      }
      setMessage(text);
      setLines(emptyLines());
      setDeliveryDate(todayIso());
      idempotencyKey.current = newIdempotencyKey('inw');
      if (result.waiting) {
        void announceSavedOffline({ what, canEnterAnother: true, note: 'It gets its document number when it is sent.' }).then(
          (choice) => {
            if (choice !== 'another') onSaved(text);
          },
        );
      }
    },
  });

  function submit(andPost: boolean) {
    setMessage(null);
    const issue = validate();
    setProblem(issue);
    if (!issue) save.mutate(andPost);
  }

  function openReview() {
    setMessage(null);
    const issue = validate();
    setProblem(issue);
    if (!issue) setReviewing(true);
  }

  const groups = supplierGroups(lines);
  const severalStores = distinctCount(lines, 'store') > 1;
  const counts = [
    `${startedLines.length} ${startedLines.length === 1 ? 'item' : 'items'}`,
    `${groups.length} ${groups.length === 1 ? 'supplier' : 'suppliers'}`,
    ...(distinctCount(lines, 'vehicleNo') > 1 ? [`${distinctCount(lines, 'vehicleNo')} vehicles`] : []),
    ...(severalStores ? [`${distinctCount(lines, 'store')} stores`] : []),
  ];

  const panel = (
    <ComposePanel
      icon="inbound"
      splitId="compose-inward"
      title={editing ? `Edit ${initial.documentNo}` : fixing ? 'Put right a refused Stock IN' : 'Receive Stock'}
      intro={
        canPost
          ? 'Nothing reaches stock until it is posted. "Receive and post" commits it now; "Save draft" keeps it for review.'
          : 'Nothing reaches stock until it is posted. Save it as a draft; someone with posting rights commits it.'
      }
      onCancel={() => {
        // Only reached once the discard was confirmed, so the draft goes too.
        draft.forget();
        onCancel();
      }}
      cancelLabel={editing ? 'Cancel' : 'Back to list'}
      bottom={
        <>
          <DraftNotice draft={draft} onStartAgain={startAgain} />
          <FormGroup title="Delivery" columns={4}>
            <Field
              label="Delivery date"
              type="date"
              required
              value={deliveryDate}
              onChange={setDeliveryDate}
              hint={
                severalSavedDates
                  ? 'This receipt had items on different dates. Saving puts them all on this date.'
                  : 'The same date for every item on this receipt.'
              }
            />
          </FormGroup>
          <FormGroup
            title="Items"
            hint={
              linesLocked
                ? 'Costs are hidden for your role, so item, quantity and cost are read-only. Supplier, store, date, vehicle, invoice and remarks can still be changed.'
                : 'Each item has its own supplier, store, vehicle and invoice. A new item copies them from the one above — change any line that differs.'
            }
          >
            <ItemLines
              lines={lines}
              onChange={changeLines}
              skus={catalog.known}
              search={catalog.search}
              showCost={showCost}
              locked={linesLocked}
              minRows={ENTRY_ROWS}
              totalsBelow={false}
              delivery={{
                suppliers: suppliers ?? [],
                canCreateSupplier: can('inv.supplier.create'),
                stores: stripStores,
                binsByStore,
              }}
              {...(editing ? {} : { lastCosts: lastCostsById })}
            />
          </FormGroup>

        </>
      }
      footer={
        <>
          {/* Pinned with the Save button, so the total stays in sight
              however long the list grows. */}
          <ItemTotals
            lines={lines}
            showCost={showCost}
            bySupplier
            summary={startedLines.length ? counts.join(' · ') : 'No items yet'}
          />
          {problem && <ErrorNote message={problem} />}
          {save.isError && <ErrorNote message={errorMessage(save.error, 'Could not save')} />}
          {message && <OkNote message={message} />}

          <FormActions onCancel={onCancel} cancelLabel={editing ? 'Cancel' : 'Close'}>
            <button type="button" className="btn btn--primary" disabled={save.isPending} onClick={openReview}>
              Review & Save
            </button>
          </FormActions>
        </>
      }
    />
  );

  const reviewWarnings = reviewing ? warnings() : [];

  return (
    <>
      {panel}
      {reviewing && (
        <ReviewSummaryDialog
          icon="inbound"
          title={editing ? `Review ${initial.documentNo}` : 'Review before receiving'}
          facts={[
            [groups.length === 1 ? 'Supplier' : 'Suppliers', groups.map((g) => supplierName(g.lines[0]!)).join(', ') || '—'],
            [
              severalStores ? 'Stores' : 'Receiving store',
              [...new Set(startedLines.map((l) => storeOf(l)).filter(Boolean).map((s) => `${s!.code} — ${s!.nameEn}`))].join(', ') || '—',
            ],
            ['Delivery date', shortDate(deliveryDate)],
          ]}
          showCost={showCost}
          lines={groups.flatMap((group) =>
            group.lines.map((l) => {
              const sku = catalog.known.get(l.skuId);
              const total = lineTotal(l);
              const bin = binsByStore.get(l.storeId)?.find((b) => b.id === l.binId);
              const where = bin ? binLabel(bin) : l.binLabel;
              return {
                item: [
                  l.skuLabel || sku?.code || l.skuId,
                  ...(severalStores ? [storeOf(l)?.code ?? ''] : []),
                  ...(where ? [where] : []),
                  ...(l.remarks.trim() ? [l.remarks.trim()] : []),
                ]
                  .filter(Boolean)
                  .join(' · '),
                qty: sku ? `${quantity(l.qty, sku.uomPrecision)}${packedSuffix(l.qty, sku)}` : l.qty,
                unitCost: l.unitCost || undefined,
                value: total === null ? undefined : total.toFixed(2),
                lot: l.batchNo || null,
                group: supplierHeading(
                  supplierName(l) || 'No supplier',
                  group.lines.map((g) => ({ invoiceRef: g.invoiceRef.trim() || null, vehicleNo: g.vehicleNo.trim() || null })),
                ),
              };
            }),
          )}
          note={reviewWarnings.length ? reviewWarnings.join(' ') : undefined}
          onClose={() => setReviewing(false)}
          primary={{
            label: canPost ? (editing ? 'Save and post' : 'Receive and post') : editing ? 'Save changes' : 'Save draft',
            pending: save.isPending,
            onClick: () => {
              setReviewing(false);
              submit(canPost);
            },
          }}
          secondary={
            canPost
              ? {
                  label: editing ? 'Save changes' : 'Save draft',
                  pending: save.isPending,
                  onClick: () => {
                    setReviewing(false);
                    submit(false);
                  },
                }
              : undefined
          }
        />
      )}
    </>
  );
}
